import { z } from "zod"

import { paginatedSchema } from "@/lib/api/pagination"

/**
 * CNI Device Registry — bir tesisteki cihazlar (Solar Inverter, BESS, PCS,
 * Wind Turbine, Grid Connection, Load).
 *
 * v1 (WP3) read-only: SuperAdmin sadece envanter yapısını görür (cihaz var mı,
 * hangi tesiste, hangi tipte, son ne zaman heartbeat aldı, son özet ölçüm).
 *
 * Real-time monitoring (canlı SCADA verileri, alarm acknowledge, dispatch)
 * KAPSAMI DIŞINDA — operasyon ekibinin işi (`docs/cni-scope.md`).
 */
export const cniDeviceTypeSchema = z.enum(["solar", "bess", "pcs", "wind", "grid", "load"])
export type CniDeviceType = z.infer<typeof cniDeviceTypeSchema>

// "unknown" = hiç telemetri basmamış katalog düğümü (rack/pack/modül) — arızalı değil,
// sadece konuşmayan cihaz; saha sağlığı hesabına katılmaz.
export const cniDeviceStatusSchema = z.enum(["online", "warning", "offline", "fault", "unknown"])
export type CniDeviceStatus = z.infer<typeof cniDeviceStatusSchema>

const nullableNumber = () =>
  z.preprocess(
    (value) => (value === null ? undefined : value),
    z.number().optional(),
  ) as z.ZodType<number | undefined>

const nullableInt = () =>
  z.preprocess(
    (value) => (value === null ? undefined : value),
    z.number().int().optional(),
  ) as z.ZodType<number | undefined>

const nullablePercent = () =>
  z.preprocess(
    (value) => (value === null ? undefined : value),
    z.number().min(0).max(100).optional(),
  ) as z.ZodType<number | undefined>

/**
 * Tip bazlı summary metrikleri. Tek union yerine optional alan kümesi —
 * MSW handler tip başına ilgili olanları doldurur, UI defansif okur.
 */
export const cniDeviceSummarySchema = z.object({
  // solar
  yieldTodayKwh: nullableNumber(),
  totalActivePowerKw: nullableNumber(),
  mpptCount: nullableInt(),
  // bess
  socPercent: nullablePercent(),
  powerKw: nullableNumber(),
  temperatureC: nullableNumber(),
  healthPercent: nullablePercent(),
  // pcs
  efficiencyPercent: nullablePercent(),
  // wind
  capacityMw: nullableNumber(),
  // grid
  voltageV: nullableNumber(),
  frequencyHz: nullableNumber(),
  // load
  demandKw: nullableNumber(),
})
export type CniDeviceSummary = z.infer<typeof cniDeviceSummarySchema>

export const cniDeviceDtoSchema = z.object({
  id: z.string(),
  facilityId: z.string(),
  facilityName: z.string(),
  firmId: z.string(),
  firmName: z.string(),
  type: cniDeviceTypeSchema,
  name: z.string(),
  serialNumber: z.string(),
  status: cniDeviceStatusSchema,
  vendor: z.string(),
  model: z.string(),
  firmwareVersion: z.string(),
  commissionedAt: z.string(),
  lastTelemetryAt: z.string(),
  summary: cniDeviceSummarySchema,
})
export type CniDeviceDto = z.infer<typeof cniDeviceDtoSchema>

export const paginatedCniDevicesSchema = paginatedSchema(cniDeviceDtoSchema)

export interface CniDevice {
  id: string
  facilityId: string
  facilityName: string
  firmId: string
  firmName: string
  type: CniDeviceType
  name: string
  serialNumber: string
  status: CniDeviceStatus
  vendor: string
  model: string
  firmwareVersion: string
  commissionedAt: string
  lastTelemetryAt: string
  summary: CniDeviceSummary
}

export function toCniDevice(dto: CniDeviceDto): CniDevice {
  return {
    id: dto.id,
    facilityId: dto.facilityId,
    facilityName: dto.facilityName,
    firmId: dto.firmId,
    firmName: dto.firmName,
    type: dto.type,
    name: dto.name,
    serialNumber: dto.serialNumber,
    status: dto.status,
    vendor: dto.vendor,
    model: dto.model,
    firmwareVersion: dto.firmwareVersion,
    commissionedAt: dto.commissionedAt,
    lastTelemetryAt: dto.lastTelemetryAt,
    summary: dto.summary,
  }
}

export interface CniDeviceListParams {
  page?: number
  pageSize?: number
  search?: string
  facilityId?: string
  firmId?: string
  type?: CniDeviceType
  status?: CniDeviceStatus
}

export const cniDeviceUpdateInputSchema = z.object({
  name: z.string().min(2).optional(),
  vendor: z.string().min(1).optional(),
  model: z.string().min(1).optional(),
  firmwareVersion: z.string().optional(),
  facilityId: z.string().optional(),
})
export type CniDeviceUpdateInput = z.infer<typeof cniDeviceUpdateInputSchema>
